/**
 * HeatingCostSection — Main container component
 *
 * Combines table, chart, price editor, and investment analysis.
 * Uses useHeatingCostConfig for state management.
 */

import { useState, useMemo } from 'react';
import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  SegmentedControl,
  Accordion,
  Badge,
  Alert,
  Divider,
} from '@mantine/core';
import type { HeatingCostSectionProps } from '../types/heatingCost.types';
import { useHeatingCostConfig } from '../hooks/useHeatingCostConfig';
import { calculateAllHeatingCosts, mapHeatingSystemToVariant } from '../utils/heatingCostCalculation';
import { analyzeInvestment } from '../utils/investmentCalculation';
import { DEFAULT_INVESTMENT_COSTS } from '../constants/investmentCosts';
import { PRICES_LAST_UPDATED, PRICES_SOURCE } from '../constants/fuelPrices';
import { HeatingCostTable } from './HeatingCostTable';
import { HeatingCostChart } from './HeatingCostChart';
import { FuelPriceEditor } from './FuelPriceEditor';
import { InvestmentAnalysis } from './InvestmentAnalysis';

type ViewMode = 'table' | 'chart';

function formatCurrency(value: number): string {
  return value.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function HeatingCostSection({
  usefulHeatingEnergy,
  hotWaterEnergy,
  currentSystem,
  buildingArea,
}: HeatingCostSectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [, setShowPriceEditor] = useState(false);

  const {
    config,
    updateFuelPrice,
    resetPrices,
  } = useHeatingCostConfig();

  // Map current heating system to fuel variant
  const currentVariant = useMemo(
    () => currentSystem ? mapHeatingSystemToVariant(currentSystem) : undefined,
    [currentSystem]
  );

  // Calculate all heating costs
  const comparison = useMemo(
    () => calculateAllHeatingCosts(
      usefulHeatingEnergy,
      hotWaterEnergy,
      config.fuelPrices,
      currentVariant
    ),
    [usefulHeatingEnergy, hotWaterEnergy, config.fuelPrices, currentVariant]
  );

  // Investment analysis: compare current system to top 3 cheapest
  const investmentAnalyses = useMemo(() => {
    if (!currentVariant) return [];

    const currentResult = comparison.results.find(r => r.variant.id === currentVariant);
    if (!currentResult) return [];

    return comparison.results
      .filter(r => r.variant.id !== currentVariant && r.annualCostGross < currentResult.annualCostGross)
      .slice(0, 3)
      .map(toResult => {
        const investmentCost = config.investmentCosts.find(c => c.variantId === toResult.variant.id)
          ?? DEFAULT_INVESTMENT_COSTS[toResult.variant.id];
        return analyzeInvestment(currentResult, toResult, investmentCost, config);
      });
  }, [comparison, currentVariant, config]);

  const totalDemand = usefulHeatingEnergy + hotWaterEnergy;

  return (
    <Paper p="xl" withBorder radius="lg" shadow="sm">
      <Stack gap="lg">
        <Title order={3}>Koszty ogrzewania — porównanie</Title>

        <Group gap="lg" wrap="wrap">
          <Text size="sm" c="dimmed">
            Zapotrzebowanie na ciepło: <strong>{formatCurrency(totalDemand)} kWh/rok</strong>
          </Text>
          {buildingArea && (
            <Text size="sm" c="dimmed">
              ({(totalDemand / buildingArea).toFixed(1)} kWh/m²/rok)
            </Text>
          )}
          {comparison.savingsVsCurrent !== undefined && comparison.savingsVsCurrent > 0 && (
            <Badge color="green" size="lg" variant="light">
              Możliwe oszczędności: {formatCurrency(comparison.savingsVsCurrent)} zł/rok
            </Badge>
          )}
        </Group>

        {/* View mode selector */}
        <Group justify="space-between">
          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={(v) => setViewMode(v as ViewMode)}
            data={[
              { label: 'Tabela', value: 'table' },
              { label: 'Wykres', value: 'chart' },
            ]}
          />
          <Text size="xs" c="dimmed">
            Ceny z: {PRICES_LAST_UPDATED} ({PRICES_SOURCE})
          </Text>
        </Group>

        {/* Main view */}
        {viewMode === 'table' ? (
          <HeatingCostTable
            comparison={comparison}
            onEditPrice={() => setShowPriceEditor(prev => !prev)}
          />
        ) : (
          <HeatingCostChart comparison={comparison} />
        )}

        {/* Price editor accordion */}
        <Accordion variant="contained">
          <Accordion.Item value="prices">
            <Accordion.Control>
              <Group gap="xs">
                <Text size="sm">Edytuj ceny paliw</Text>
                {config.fuelPrices.some(p => p.isUserModified) && (
                  <Badge size="xs" color="orange" variant="light">zmodyfikowane</Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <FuelPriceEditor
                prices={config.fuelPrices}
                onUpdatePrice={updateFuelPrice}
                onReset={resetPrices}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Investment analysis */}
        {investmentAnalyses.length > 0 && (
          <>
            <Divider />
            <Title order={4}>Analiza opłacalności zmiany systemu</Title>
            <Text size="xs" c="dimmed">
              Porównanie z obecnym systemem grzewczym — top 3 najtańsze alternatywy
            </Text>
            <Stack gap="md">
              {investmentAnalyses.map(analysis => (
                <InvestmentAnalysis key={analysis.toVariant} analysis={analysis} />
              ))}
            </Stack>
          </>
        )}

        {!currentVariant && (
          <Alert color="blue" variant="light" title="Wskazówka">
            Aby zobaczyć analizę opłacalności zmiany systemu grzewczego,
            wybierz obecny system ogrzewania w formularzu symulatora.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
